import React from 'react';
import { Col, Form, FormGroup, Row } from 'react-bootstrap';
import {
  ExtraFieldFormInfoFragment,
  FieldType,
  RecordTypeFragment,
} from '../../../__generated__/graphql';
import { FieldValue } from '../../EmployeeData/FieldValue';
import { FieldOption, NO_OPTIONS } from '../../EmployeeData/types';
import Skeleton from 'react-loading-skeleton';
import { recordTypeFormKey } from '../logic';
import i18next from 'i18next';
import { ExtraFieldsVisibility } from '../extraFieldsVisibility';
import { ExtraFieldContextParam } from '../types';
import StringToHtml from '../../../../../components/StringToHtml';
import {
  ExtraFieldInternalValue,
  toDisplayValue,
} from '../extraFieldInternalValue';

type Props = {
  formFields: readonly ExtraFieldFormInfoFragment[];
  values: readonly ExtraFieldInternalValue[];
  recordType?: RecordTypeFragment;
  columnsCount?: number;
  renderItems?: (items: ExtraFieldsValueToRender[]) => React.ReactNode;
  contextParams?: ExtraFieldContextParam[];
  hideRecordType?: boolean;
};

export type ExtraFieldsValueToRender = {
  inputValue: string | null;
  fieldType: FieldType;
  label: string;
  fieldApiName: string;
  isRichTextArea?: boolean;
  options: readonly FieldOption[];
};

export const ExtraFieldsViewAreaContent: React.FC<
  React.PropsWithChildren<Props>
> = ({
  formFields,
  values,
  recordType,
  columnsCount = 1,
  renderItems,
  contextParams = [],
  hideRecordType = false,
}) => {
  let valuesToRender = [];
  if (!hideRecordType && recordType && !recordType.IsMaster) {
    valuesToRender.push({
      inputValue: recordType.Name,
      fieldType: FieldType.Picklist,
      label: i18next.t('extraFields.recordType'),
      fieldApiName: recordTypeFormKey,
      options: NO_OPTIONS,
    });
  }

  const visibility = new ExtraFieldsVisibility();
  const visibleFormFields = formFields.filter((f) =>
    visibility.isFieldVisible(f, {
      recordTypeName: recordType?.DeveloperName ?? null,
      contextParams,
    }),
  );

  valuesToRender = [
    ...valuesToRender,
    ...visibleFormFields.reduce((acc, formInfo) => {
      const value = values.find(
        (v) => v.fieldApiName === formInfo.fieldApiName,
      );
      if (value) {
        acc.push({
          fieldApiName: formInfo.fieldApiName,
          inputValue: toDisplayValue(value),
          label: formInfo?.fieldInfo?.label,
          fieldType: formInfo?.fieldInfo?.type,
          isRichTextArea: formInfo?.fieldInfo?.isRichTextArea,
          options: formInfo?.fieldInfo?.picklistValues ?? NO_OPTIONS,
        });
      }
      return acc;
    }, new Array<ExtraFieldsValueToRender>()),
  ];

  if (renderItems) {
    return <>{renderItems(valuesToRender)}</>;
  }

  const rowsCount = Math.ceil(valuesToRender.length / columnsCount);
  return (
    <>
      {Array.from(Array(rowsCount).keys()).map((rowIndex) => {
        return (
          <Row key={rowIndex.toString()}>
            {valuesToRender
              .slice(rowIndex * columnsCount, (rowIndex + 1) * columnsCount)
              .map(
                (
                  {
                    label,
                    inputValue,
                    fieldApiName,
                    fieldType,
                    isRichTextArea,
                    options,
                  },
                  colIndex,
                ) => {
                  return (
                    <Col key={fieldApiName}>
                      <FormGroup>
                        <Form.Label className="fw-bold">{label}</Form.Label>
                        <p>
                          {isRichTextArea && inputValue ? (
                            <StringToHtml text={inputValue} />
                          ) : (
                            <FieldValue
                              name={fieldApiName}
                              type={fieldType}
                              options={options}
                              value={inputValue}
                            />
                          )}
                        </p>
                      </FormGroup>
                    </Col>
                  );
                },
              )}
          </Row>
        );
      })}{' '}
    </>
  );
};

export const Loading: React.FC<
  React.PropsWithChildren<{ columnsCount: number }>
> = ({ columnsCount }) => {
  const rowsCount = Math.ceil(6 / columnsCount);
  return (
    <>
      {Array.from(Array(rowsCount).keys()).map((rowIndex) => {
        return (
          <Row key={rowIndex.toString()}>
            {Array.from(Array(columnsCount).keys()).map((columnIndex) => {
              return (
                <Col key={columnIndex.toString()}>
                  <Skeleton height={40} className="mt-3" />
                </Col>
              );
            })}
          </Row>
        );
      })}
    </>
  );
};
